import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { canCreateInterestGroup } from "@/helpers/permissions";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { List } from "@/modules/community-groups/components/List/List";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function InterestGroupListPage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const hasPermission = usePermission();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.interest-group.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.interest-group.list.title"),
            },
          ]}
        />
      }
      actions={
        <>
          <AskNobiButton
            defaultFilters={{ projectIds: [projectId] }}
            context={{
              scope: "project",
              entity: "interest-groups",
              page: "list",
            }}
            suggestions={[
              t("component.ask-nobi-chat-drawer.interest-groups.suggestion.1"),
              t("component.ask-nobi-chat-drawer.interest-groups.suggestion.2"),
            ]}
          />
          {hasPermission(canCreateInterestGroup) && (
            <Button
              type="link"
              data-testid="group-action-create"
              href={
                sessionUser.isAdmin
                  ? routes.interestGroups.create({ slug })
                  : routes.interestGroups.createResidentGroup({ slug })
              }
            >
              {t("model.interest-group.action.create")}
            </Button>
          )}
        </>
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="100%">
          <List
            type="interest"
            followLabels={{
              button: t("model.interest-group.action.follow"),
              failedNotification: t("model.interest-group.action.follow.notification-error"),
            }}
            unfollowLabels={{
              button: t("model.interest-group.action.unfollow"),
              failedNotification: t("model.interest-group.action.unfollow.notification-error"),
            }}
            allAudienceLabel={t("model.interest-group.audience.placeholder")}
            deleteLabels={{
              confirmation: t("model.interest-group.action.delete.confirmation"),
              description: t("model.interest-group.action.delete.description"),
              success: t("model.interest-group.action.delete.notification.success"),
              error: t("model.interest-group.action.delete.notification.error"),
            }}
            statusLabels={{
              disable: {
                confirmation: t("model.platform.interest-group.action.disable.confirmation"),
                description: t("model.platform.interest-group.action.disable.description"),
                success: t("model.platform.interest-group.action.disable.notification.success"),
                error: t("model.platform.interest-group.action.disable.notification.error"),
              },
              enable: {
                success: t("model.platform.interest-group.action.enable.notification.success"),
                error: t("model.platform.interest-group.action.enable.notification.error"),
              },
            }}
          />
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}
